import React from 'react';
import {useParams, Link} from 'react-router-dom';
import {Box, CircularProgress, Grid, Typography, Button, Chip, Drawer, IconButton} from '@mui/material';
import {ArrowForward as ArrowForwardIcon, Close as CloseIcon} from '@mui/icons-material';
import {UserPilotDetailsOverview, UserPilotOverviewData} from '../../views/pilots/UserPilotDetailsOverview';
import {EquilibriumStatusCard} from '../../views/pilots/EquilibriumStatusCard';
import {useQueryAsUser, useQueryCoordinate} from '../../clients/graphql/graphql.hooks';
import {TriggerHistoryTable} from '../../components/tables/TriggerHistoryTable';
import {BalancingDecisionsTableV2} from '../../components/tables/BalancingDecisionsTableV2';
import {FailedAutopilotOrdersTable} from '../../components/tables/FailedAutopilotOrdersTable';
import {BalancePortfolioButton} from "../../components/buttons/BalancePortfolioButton";
import {UndoAllocationAdjustmentButton} from "../../components/buttons/UndoAllocationAdjustmentButton";
import {PortfolioRestrictionsTable} from '../../components/tables/PortfolioRestrictionsTable';
import {
    JediFetchPendingOrders,
    SlaveAutoPilotPortfolio,
    userPilotDetailsQuery,
    forcedApprovalSettingsQuery,
    equilibriumStatusesQuery,
    whoisit
} from "../../clients/graphql/pages/users-queries";
import {useUserPortfolioLpoaFetch} from "../../clients/graphql/pages/lpoa-queries";
import {DeleteAutoPilotSettingsButton} from '../../components/buttons/DeleteAutoPilotSettingsButton';
import RestorePilot from "../../components/buttons/RestorePilotButton";
import {PendingOrdersTable} from '../../components/tables/PendingOrdersTable';

interface ForcedApprovalSettings {
    forcedManualApproval?: boolean;
    forcedNegativeConsentApproval?: boolean;
}

const mappedData = (details: SlaveAutoPilotPortfolio[], forcedApprovalSettings?: ForcedApprovalSettings) => {
    const mapped: UserPilotOverviewData = details.map((detail: SlaveAutoPilotPortfolio) => ({
        userKey: detail.slaveUserKey,
        autoPilotSettingsKey: detail.autoPilotSettingsKey,
        pilot: detail.masterPortfolio.userFullName,
        allocation: detail.autoPilotSettings.allocationAmount,
        status: detail.autoPilotSettings.setupStatus,
        portfolio: {
            brokerName: detail.autoPilotSettings.slavePortfolio.brokerName,
            connectionStatus: detail.autoPilotSettings.slavePortfolio.connectionStatus,
            portfolioKey: detail.autoPilotSettings.slavePortfolio.portfolioKey,
        },
        portfolioConnectionState: detail.autoPilotSettings.slavePortfolio.connectionStatus,
        amountPending: detail.autoPilotSettings.amountPending,
        initiatedAt: detail.autoPilotSettings.initiatedAt,
        equilibriumState: '',
        isManualExecution: detail.isManualExecution,
        shortCircuited: detail.autoPilotSettings.shortCircuited,
        forcedManualApproval: forcedApprovalSettings?.forcedManualApproval,
        forcedNegativeConsentApproval: forcedApprovalSettings?.forcedNegativeConsentApproval
    }))[0];
    return mapped;
}

export const PilotDetailsPage: React.FC = () => {
    const {autoPilotSettingsKey} = useParams() || '';
    const [showJsonData, setShowJsonData] = React.useState(false);
    const hasLoadedRef = React.useRef(false);
    const lastMappedRef = React.useRef<UserPilotOverviewData | undefined>(undefined);
    const whoisitResponse = useQueryCoordinate(whoisit, {variables: {autoPilotSettingsKey: parseInt(autoPilotSettingsKey as string)}, errorPolicy: "all"});
    const pendingOrdersResponse = useQueryCoordinate(JediFetchPendingOrders, {variables: {apsSettingsKey: parseInt(autoPilotSettingsKey as string)}});
    const userKey = whoisitResponse.data?.whoisit?.user?.userKey;
    const parsedSettingsKey = parseInt(autoPilotSettingsKey as string);
    const {data, loading, error} = useQueryAsUser(userPilotDetailsQuery, {
        userKey,
        autoPilotSettingsKey: parsedSettingsKey,
        variables: {userKey, autoPilotSettingsKey: parsedSettingsKey},
        errorPolicy: 'all',
        returnPartialData: true,
    });
    
    // Fetch LPOA data
    const {data: lpoaData} = useUserPortfolioLpoaFetch({
        userKey: userKey || 0
    });

    // Fetch forced approval settings from autoPilotSettingsGet
    const {data: forcedApprovalData} = useQueryAsUser(forcedApprovalSettingsQuery, {
        userKey,
        variables: {autoPilotSettingsKey: parsedSettingsKey}
    });

    // Fetch equilibrium status for this autoPilotSettings
    const {data: equilibriumData, loading: equilibriumLoading} = useQueryAsUser(equilibriumStatusesQuery, {
        userKey,
        variables: {autoPilotSettingsKey: parsedSettingsKey},
        skip: !userKey,
        errorPolicy: 'all',
    });
    
    // Create the encoded filter URL for the workflow page
    const workflowFilter = encodeURIComponent(JSON.stringify([{
        key: "autoPilotSettingsKey",
        value: autoPilotSettingsKey
    }]));
    const workflowUrl = `/developer/workflows/BalancingLegacyWorkflow?filters=${workflowFilter}`;
    
    const details = data?.slaveAutoPilotPortfoliosGet?.slaveAutoPilotPortfolios ?? [];
    if (details.length > 0 && userKey) hasLoadedRef.current = true;
    if (!hasLoadedRef.current && ((loading && details.length === 0) || !userKey)) return <CircularProgress size={60} sx={{ marginTop: '80px', marginLeft: '50%'}} />;
    if (error && details.length === 0) return <div>Error: {error.message}</div>;
    const setupStatus = details[0]?.autoPilotSettings?.setupStatus;
    const pendingOrders = pendingOrdersResponse?.data?.jediFetchPendingOrders
    const forcedApprovalSettings = forcedApprovalData?.autoPilotSettingsGet?.autoPilotSettings?.[0];
    const equilibriumStatus = equilibriumData?.equilibriumStatusesGet?.equilibriumStatuses?.[0];
    const freshMapped = mappedData(details, forcedApprovalSettings);
    if (freshMapped) lastMappedRef.current = freshMapped;
    const mapped = freshMapped ?? lastMappedRef.current;
    if (!mapped) return <div>No data</div>;

    return <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" paddingBottom={2}>
            <Typography variant="h4">{mapped.pilot}</Typography>
            <Typography
                component={Link}
                to={workflowUrl}
                variant="body2"
                sx={{
                    color: 'grey.600',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': {
                        textDecoration: 'underline'
                    }
                }}
            >
                View Balancing Attempts <ArrowForwardIcon fontSize="small" />
            </Typography>
        </Box>
        
        <Box display={'flex'} gap={1} flexWrap="wrap">
            <BalancePortfolioButton autoPilotSettingsKey={parsedSettingsKey} />
            {// If the pilot is deleted it renders the Restore button, otherwise the delete button is displayed.
                setupStatus === "DELETED" ? (
                <RestorePilot autoPilotSettingsKey={parsedSettingsKey!} /> ) :
                (<DeleteAutoPilotSettingsButton autoPilotSettingsKey={parsedSettingsKey} userKey={userKey}/>
            )}
        </Box>
        
        {/* JSON Data Drawer */}
        {showJsonData && (
            <Drawer
                anchor="right"
                open={showJsonData}
                onClose={() => setShowJsonData(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: '50%',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        borderLeft: '1px solid',
                        borderColor: 'grey.300',
                        backgroundColor: 'white',
                        p: 3,
                        transition: 'transform 0.3s ease-in-out',
                    },
                    '& .MuiBackdrop-root': {
                        transition: 'opacity 0.3s ease-in-out',
                    },
                }}
                transitionDuration={300}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" fontWeight={600}>
                        Balancing State Data
                    </Typography>
                    <IconButton onClick={() => setShowJsonData(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Balancing State Metadata
                </Typography>
                <Box 
                    component="pre" 
                    sx={{ 
                        backgroundColor: 'grey.50',
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.300',
                        overflow: 'auto',
                        maxHeight: '400px',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: 1.5,
                    }}
                >
                    {JSON.stringify(details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].data, null, 2)}
                </Box>
            </Drawer>
        )}
        
        <Grid container spacing={2}>
            {/* Left Column - Customer Overview and Latest Balancing State */}
            <Grid item xs={3}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Customer Overview */}
                    <UserPilotDetailsOverview data={mapped} lpoaData={lpoaData?.UserPortfolioLpoaFetch} />

                    {/* Equilibrium Status */}
                    <EquilibriumStatusCard status={equilibriumStatus} loading={equilibriumLoading} />

                    {/* Latest Balancing State */}
                    {details[0]?.autoPilotSettings?.balancingStatesConnection?.total > 0 && 
                     details[0]?.autoPilotSettings?.balancingStatesConnection?.balancingStates?.[0] && (
                        <Box sx={{ 
                            width: '100%',
                            p: 2.5, 
                            bgcolor: 'white',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.1)',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '2px',
                                background: 'linear-gradient(90deg, #007AFF, #5856D6, #FF2D92)',
                            }
                        }}>
                            <Typography variant="h6" gutterBottom sx={{ 
                                mb: 2, 
                                fontWeight: 700, 
                                color: 'grey.800',
                                fontSize: '1rem',
                                letterSpacing: '-0.02em'
                            }}>
                                Latest Balancing State
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {/* Status Row */}
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column',
                                    gap: 1,
                                    p: 1.5,
                                    bgcolor: 'grey.50',
                                    borderRadius: 2,
                                    border: '1px solid',
                                    borderColor: 'grey.100'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            color: 'grey.600'
                                        }}>
                                            State
                                        </Typography>
                                        <Chip 
                                            label={details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].stateType || 'Unknown'} 
                                            color={details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].stateType === 'TERMINAL' ? 'success' : 'warning'}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                height: '22px',
                                                '& .MuiChip-label': {
                                                    px: 1,
                                                    fontSize: '0.7rem',
                                                },
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                borderRadius: '6px',
                                            }}
                                        />
                                    </Box>
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ 
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            color: 'grey.600'
                                        }}>
                                            Work Required
                                        </Typography>
                                        <Chip 
                                            label={details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType || 'Unknown'} 
                                            color={
                                                details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType === 'NO_WORK_REQUIRED' ? 'success' :
                                                details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType === 'PLATFORM' ? 'primary' :
                                                details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType === 'USER' ? 'warning' :
                                                details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType === 'BROKER' ? 'error' : 'default'
                                            }
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                height: '22px',
                                                '& .MuiChip-label': {
                                                    px: 1,
                                                    fontSize: '0.7rem',
                                                },
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                borderRadius: '6px',
                                            }}
                                        />
                                    </Box>
                                </Box>
                                
                                {/* Actions */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Button
                                        component={Link}
                                        to={`/developer/workflows/BalancingLegacyWorkflow/executions/${details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workflowId}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            px: 1.5,
                                            py: 0.5,
                                            height: '28px',
                                            fontSize: '0.7rem',
                                            borderRadius: '6px',
                                            borderColor: 'grey.300',
                                            color: 'grey.700',
                                            '&:hover': {
                                                borderColor: 'grey.400',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                            }
                                        }}
                                    >
                                        View Workflow
                                    </Button>
                                    
                                    <Button
                                        component={Link}
                                        to={`/users/balancing-states/${autoPilotSettingsKey}`}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                            px: 1.5,
                                            py: 0.5,
                                            height: '28px',
                                            fontSize: '0.7rem',
                                            borderRadius: '6px',
                                            borderColor: 'grey.300',
                                            color: 'grey.700',
                                            '&:hover': {
                                                borderColor: 'grey.400',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                            }
                                        }}
                                    >
                                        View All States
                                    </Button>
                                    
                                    {/* Show JSON Data Button */}
                                    {details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].workRequiredAttributionStateType !== 'NO_WORK_REQUIRED' && (
                                        <Button
                                            onClick={() => setShowJsonData(!showJsonData)}
                                            variant="contained"
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                px: 1.5,
                                                py: 0.5,
                                                height: '28px',
                                                fontSize: '0.7rem',
                                                borderRadius: '6px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                                }
                                            }}
                                        >
                                            {showJsonData ? 'Hide' : 'Show'} Data
                                        </Button>
                                    )}
                                </Box>
                                
                                <Typography variant="caption" color="text.secondary" sx={{ 
                                    fontSize: '0.7rem',
                                    color: 'grey.500',
                                    fontWeight: 500,
                                    textAlign: 'center'
                                }}>
                                    Created: {new Date(details[0].autoPilotSettings.balancingStatesConnection.balancingStates[0].createdAt).toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Grid>
            
            {/* Right Column - Balancing Decisions */}
            <Grid item xs={9}>
                <BalancingDecisionsTableV2
                    userKey={parseInt(userKey as string)}
                    autoPilotSettingsKey={parseInt(autoPilotSettingsKey as string)}
                />
            </Grid>
        </Grid>
        <Grid item xs={12} sx={{marginBottom: '1rem', paddingTop: '2rem'}}>
            {userKey && (
                <Box display="flex" justifyContent="flex-end" sx={{ mb: 1 }}>
                    <UndoAllocationAdjustmentButton
                        autoPilotSettingsKey={parsedSettingsKey}
                        userKey={parseInt(userKey as string)}
                    />
                </Box>
            )}
                <TriggerHistoryTable info={{
                    userKey: parseInt(userKey as string),
                    autoPilotSettingsKey: parsedSettingsKey,
                    allocationAmount: mapped.allocation,
                    isUserView: false,
                }}/>

            {pendingOrders?.length > 0 && (
              <Grid item xs={4} sx={{marginBottom: '1rem', paddingTop: '1rem'}}>
                  <PendingOrdersTable pendingOrders={pendingOrders} autoPilotSettingsKey={parsedSettingsKey}/>
              </Grid>
            )}

            <Grid item xs={6} sx={{marginBottom: '1rem', paddingTop: '1rem'}}>
                    <FailedAutopilotOrdersTable sx={{marginBottom: '1rem', paddingTop: '.5rem'}} autoPilotSettingsKey={parseInt(autoPilotSettingsKey as string)}/>
            </Grid>
            <Grid item xs={6}>
                    <PortfolioRestrictionsTable userKey={parseInt(userKey as string)} portfolioKey={mapped.portfolio.portfolioKey} />
            </Grid>
        </Grid>
       
    </Box>;
};
